import React from "react";
import { ArrowLeft, Clock, User } from "lucide-react";
import { Button } from "./ui/button";

const BlogPost4 = ({ onBack }) => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Button 
        variant="ghost" 
        onClick={onBack}
        className="mb-8 text-credentra-bright-teal hover:text-credentra-dark-teal"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Blog
      </Button>
      
      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-credentra-dark-teal mb-4">
            Why Your Restaurant's Money Is Sitting in the IRS Vault (And How to Get It Back)
          </h1>
          <div className="flex items-center gap-4 text-gray-600 text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              4 min read
            </div>
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              Credentra Team
            </div>
          </div>
        </header>

        <div className="space-y-6 text-gray-700 leading-relaxed">
          <p className="text-xl text-gray-600 font-medium">
            Every time you pay taxes on your employees' tips, you're essentially making an interest-free loan to the IRS. Here's how restaurant owners are finally getting that money back—and why you should be next.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The Hidden Cost of Running a Tipped Business</h2>
          <p>
            Here's something most restaurant owners don't realize: when your server gets a $50 tip, you don't just hand them the money and call it even. The IRS requires you to pay an additional $3.83 in employer taxes on that tip (7.65% of the tip amount).
          </p>

          <p>
            Think about that for a second. Your customer tips your employee, but <em>you</em> pay the government for the privilege. It's like being charged a fee every time someone compliments your service.
          </p>

          <div className="bg-gray-50 p-6 rounded-lg my-6">
            <h3 className="font-bold text-credentra-dark-teal mb-3">Quick Math Reality Check:</h3>
            <ul className="space-y-2">
              <li>• If your restaurant processes $200K in tips annually, you're paying ~$15,300 in extra taxes</li>
              <li>• That's enough to cover 2-3 months of utilities</li>
              <li>• Or fund equipment repairs for a whole year</li>
              <li>• Or give every employee a nice holiday bonus</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The IRS Knows This Isn't Fair</h2>
          <p>
            Here's the plot twist: the IRS actually agrees this system is problematic. That's why they created the FICA tip credit—a dollar-for-dollar tax credit that lets you get back every penny of those employer taxes you paid on tips.
          </p>

          <p>
            But here's the catch (there's always a catch): they don't automatically give it to you. You have to know about it, calculate it correctly, and file the right paperwork. Most restaurant owners never even hear about this credit, let alone claim it.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Why 90% of Restaurants Leave This Money on the Table</h2>
          <p>
            The reasons are frustratingly simple:
          </p>

          <ul className="list-disc pl-6 space-y-3">
            <li><strong>Nobody tells you about it</strong> - Your POS company doesn't mention it. Your payroll provider doesn't bring it up. Even some accountants don't know about it.</li>
            <li><strong>The paperwork looks scary</strong> - IFS forms, FICA calculations, tip allocation schedules... it feels like you need a tax degree just to understand what they're asking for.</li>
            <li><strong>Your data is scattered</strong> - Tips are in your POS system, payroll is with another provider, and somehow you're supposed to make them match perfectly.</li>
            <li><strong>You're already busy running a restaurant</strong> - Between managing staff, dealing with suppliers, and keeping customers happy, who has time for tax strategy?</li>
          </ul>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The Real Cost of Waiting</h2>
          <p>
            Every year you don't claim this credit, you're leaving money on the table. But it gets worse: you can only go back three years on amended returns. That means any unclaimed credits from 2021 and earlier are gone forever.
          </p>

          <p>
            Let's put this in perspective. If you've been missing out on $10K per year in tip credits for the past five years:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>You can recover: $30K (2022, 2023, 2024)</li>
            <li>You've permanently lost: $20K (2019, 2020, 2021)</li>
          </ul>

          <div className="bg-credentra-bright-teal/10 border-l-4 border-credentra-bright-teal p-6 my-8">
            <h3 className="font-semibold text-credentra-dark-teal mb-2">The Reality Check:</h3>
            <p>
              Many restaurant owners discover they've been missing out on significant tax credits for years. The combination of not knowing about the credit and the complexity of claiming it means thousands of dollars often go unclaimed. The earlier you start, the more you can recover.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">What Getting This Right Actually Looks Like</h2>
          <p>
            When you properly claim FICA tip credits, here's what happens:
          </p>

          <ol className="list-decimal pl-6 space-y-3">
            <li><strong>Immediate cash flow improvement</strong> - The credit reduces your quarterly tax payments or triggers a refund</li>
            <li><strong>Ongoing monthly benefits</strong> - Once set up, you can track and claim credits every quarter</li>
            <li><strong>Historical recovery</strong> - File amended returns for up to three prior years of missed credits</li>
            <li><strong>Peace of mind</strong> - Know you're getting every tax benefit you're legally entitled to</li>
          </ol>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The Simple Truth About Implementation</h2>
          <p>
            You don't need to become a tax expert to benefit from tip credits. You just need clean data and someone who knows how to present it to the IRS correctly. That's it.
          </p>

          <p>
            Modern systems can connect to whatever POS and payroll you're already using, reconcile the data automatically, and generate the proper forms. No software changes, no new processes for your team, no disruption to your operations.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Your Next Move</h2>
          <p>
            Every month you wait is another month of unclaimed credits. If you've been in business for more than a year and your employees receive tips, you almost certainly qualify for this credit.
          </p>

          <p>
            The question isn't whether you can afford to implement tip credit recovery. The question is whether you can afford not to. That money is sitting in the IRS vault with your name on it—it's time to go get it.
          </p>

          <div className="bg-credentra-bright-teal/10 border-l-4 border-credentra-bright-teal p-6 my-8">
            <p className="font-semibold text-credentra-dark-teal mb-2">Important Note:</p>
            <p className="text-sm">
              Dollar amounts mentioned are hypothetical examples for illustration only. Actual credits depend on your specific business circumstances, employee tip reporting, and other factors. Always consult with a qualified tax professional regarding your specific situation.
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost4;